import React from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import MaterialSymbol from '../common/MaterialSymbol';
import { useTranslate } from 'react-admin';
import { BadgeProgress, UserBadge } from '../../services/gamificationService';

interface BadgeCardProps {
    badge: BadgeProgress | UserBadge;
    size?: 'small' | 'medium' | 'large';
    showProgress?: boolean;
}

const BadgeCard: React.FC<BadgeCardProps> = ({
    badge,
    size = 'medium',
    showProgress = true,
}) => {
    const translate = useTranslate();

    // Determine if this is a BadgeProgress or UserBadge
    const isBadgeProgress = 'isEarned' in badge;
    const isEarned = isBadgeProgress ? badge.isEarned : true;
    const badgeData = isBadgeProgress ? badge : badge.badge;
    const progress = isBadgeProgress ? badge.progress : null;
    const target = isBadgeProgress ? badge.target : null;

    const iconSize = size === 'small' ? 32 : size === 'medium' ? 48 : 64;
    const cardMinWidth = size === 'small' ? 100 : size === 'medium' ? 140 : 180;

    const progressPercent = progress !== null && target !== null && target > 0
        ? Math.min((progress / target) * 100, 100)
        : 0;

    return (
        <Card
            sx={{
                minWidth: cardMinWidth,
                opacity: isEarned ? 1 : 0.6,
                filter: isEarned ? 'none' : 'grayscale(50%)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 3,
                },
            }}
        >
            <CardContent sx={{ textAlign: 'center', p: size === 'small' ? 1 : 2 }}>
                <Tooltip title={badgeData.description || ''}>
                    <Box
                        sx={{
                            width: iconSize,
                            height: iconSize,
                            borderRadius: '50%',
                            bgcolor: isEarned ? 'primary.main' : 'grey.300',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 1,
                        }}
                    >
                        <MaterialSymbol
                            name={badgeData.icon || 'emoji_events'}
                            size={iconSize * 0.6}
                            filled
                            style={{ color: isEarned ? '#fff' : '#9e9e9e' }}
                        />
                    </Box>
                </Tooltip>

                <Typography
                    variant={size === 'small' ? 'caption' : 'body2'}
                    fontWeight="medium"
                    noWrap
                >
                    {badgeData.name}
                </Typography>

                {showProgress && !isEarned && progress !== null && target !== null && (
                    <Box sx={{ mt: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progressPercent}
                            sx={{ height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {progress}/{target}
                        </Typography>
                    </Box>
                )}

                {isEarned && !isBadgeProgress && 'awardedAt' in badge && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {new Date(badge.awardedAt).toLocaleDateString()}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default BadgeCard;
